import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import ErrorMessage from '@components/Common/ErrorMessage';
import type { Column } from '@components/Common/SearchFilter/SearchFilter';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDate } from '@helpers/date';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const ALERT_COLUMNS = ['name', 'phone', 'citiesDisplay', 'industriesDisplay', 'createdAt', 'actions'];

interface FreeJobAlert {
  _id: string;
  name?: string;
  phone: string;
  cities?: string[];
  industries?: string[];
  createdAt?: string;
  updatedAt?: string;
}

const ROW_PER_PAGE = 50;
const FILTER_COLUMNS = ['Name', 'Phone', 'City'];

const FreeJobAlerts: React.FC = () => {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<FreeJobAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filterData, setFilterData] = useState<Column[]>([]);
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const skipResetOnFirstFilterChange = useRef(true);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/free-job-alerts');
      setData(response.data?.content || []);
    } catch {
      setError('Failed to fetch free job alerts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await axios.delete(`/api/free-job-alerts/${id}`);
      showToast('Alert deleted successfully', 'success');
      await fetchAlerts();
    } catch {
      showToast('Failed to delete alert', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const onApplyFilters = useCallback(() => {
    if (skipResetOnFirstFilterChange.current) {
      skipResetOnFirstFilterChange.current = false;
      return;
    }
    setPage(0);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('page'); return n; });
  }, []);

  const filteredRows = useMemo(() => {
    const nameSearch = filterData.find(f => f.columnName === 'Name')?.searchValue?.toLowerCase() || '';
    const phoneSearch = filterData.find(f => f.columnName === 'Phone')?.searchValue?.toLowerCase() || '';
    const citySearch = filterData.find(f => f.columnName === 'City')?.searchValue?.toLowerCase() || '';

    return data
      .filter(a => {
        if (nameSearch && !(a.name || '').toLowerCase().includes(nameSearch)) return false;
        if (phoneSearch && !a.phone.toLowerCase().includes(phoneSearch)) return false;
        if (citySearch && !(a.cities || []).some(c => c.toLowerCase().includes(citySearch))) return false;
        return true;
      })
      .map(a => ({
        id: a._id,
        name: a.name || '-',
        phone: a.phone,
        citiesDisplay: a.cities?.length ? a.cities.join(', ') : '-',
        industriesDisplay: a.industries?.length ? a.industries.join(', ') : '-',
        createdAt: a.createdAt,
      }));
  }, [data, filterData]);

  const pageRows = useMemo(
    () => filteredRows.slice(page * ROW_PER_PAGE, (page + 1) * ROW_PER_PAGE),
    [filteredRows, page],
  );

  return (
    <CRMPageTemplate
      filterColumns={FILTER_COLUMNS}
      filterLabel="Filter"
      icon={<NotificationsActiveIcon />}
      setFilterData={setFilterData}
      title="Free Job Alerts"
      totalCount={filteredRows.length}
      onApplyFilters={onApplyFilters}
    >
      {loading ? (
        <SkeletonLoader rows={20} columns={ALERT_COLUMNS.length} />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : filteredRows.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
          <Typography color="text.secondary" variant="body2">No free job alerts found.</Typography>
        </Box>
      ) : (
        <MintlyTable
          pagination={{
            enabled: true,
            page,
            rowsPerPage: ROW_PER_PAGE,
            totalNumberOfRows: filteredRows.length,
            handleChangePage: (_: unknown, newPage: number) => { setPage(newPage); setSearchParams(prev => { const n = new URLSearchParams(prev); if (newPage > 0) n.set('page', String(newPage)); else n.delete('page'); return n; }); },
          }}
          rows={pageRows}
          selectors={ALERT_COLUMNS}
          tableHeadRow={{
            name: <>Name</>,
            phone: <>Phone</>,
            citiesDisplay: <>Cities</>,
            industriesDisplay: <>Industries</>,
            createdAt: <>Created</>,
            actions: <></>,
          }}
          tableBodyRow={{
            name: (row: any) => <>{row.name}</>,
            phone: (row: any) => <>{row.phone}</>,
            citiesDisplay: (row: any) => <>{row.citiesDisplay}</>,
            industriesDisplay: (row: any) => <>{row.industriesDisplay}</>,
            createdAt: (row: any) => (
              <>{formatDate(row.createdAt)}</>
            ),
            actions: (row: any) => (
              <Tooltip title="Delete alert">
                <span>
                  <IconButton
                    color="error"
                    disabled={deletingId === row.id}
                    size="small"
                    onClick={e => { e.stopPropagation(); handleDelete(row.id); }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ),
          }}
        />
      )}
    </CRMPageTemplate>
  );
};

export default FreeJobAlerts;
